// src/features/simulators/shared/components/SimulatorWizard.tsx

import React from 'react';
import {
  Box,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  PlayArrow as RunIcon,
  Refresh as ResetIcon,
  Stop as StopIcon,
  Hotel as HotelIcon,
  Router as DeviceIcon,
} from '@mui/icons-material';
import { useTranslate, useGetList } from 'react-admin';
import { TextField, MenuItem } from '@mui/material';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { DeviceSelector } from './DeviceSelector';
import { SimulationProgress } from './SimulationProgress';
import { SimulationResults } from './SimulationResults';
import type { useSimulatorWizard } from '../hooks/useSimulatorWizard';

type WizardState = ReturnType<typeof useSimulatorWizard>;

interface SimulatorWizardProps {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  wizard: WizardState;
  /** Device type filter for DeviceSelector */
  deviceType?: string;
  /** Channel filter for DeviceSelector */
  channel?: string;
  /** Step 2 config form content */
  configForm: React.ReactNode;
  /** Extra content in results (simulator-specific stats) */
  resultsExtra?: React.ReactNode;
  /** Callback to run the simulation (parent triggers wizard.runSimulation) */
  onRun: () => void;
}

const STEP_LABELS = [
  'resources.simulators.wizard.step_device',
  'resources.simulators.wizard.step_config',
  'resources.simulators.wizard.step_run',
] as const;

export const SimulatorWizard: React.FC<SimulatorWizardProps> = ({
  title,
  subtitle,
  icon,
  wizard,
  deviceType,
  channel,
  configForm,
  resultsExtra,
  onRun,
}) => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: hotels, isLoading: hotelsLoading } = useGetList('hotels', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  const isRunning = wizard.simulationStatus === 'running' || wizard.simulationStatus === 'preparing';
  const isCompleted = wizard.simulationStatus === 'completed' || wizard.simulationStatus === 'failed';

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      minHeight: 'calc(100vh - 200px)',
      px: { xs: 1.5, sm: 2, md: 2.5 },
      py: 3,
    }}>
      <Box sx={{
        width: '100%',
        maxWidth: wizard.activeStep === 2
          ? { xs: '100%', md: '1000px', lg: '1200px', xl: '1400px' }
          : { xs: '100%', md: '800px' },
        transition: 'max-width 0.3s ease',
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 1,
            backgroundColor: `${theme.palette.primary.main}10`,
            color: theme.palette.primary.main,
          }}>
            {icon}
          </Box>
          <Box>
            {/* Page-level h1 + tertiary subtitle — the app's own PageTitle
                recipe, same as every list/dashboard screen, instead of a
                small bold line one level below it (F-B-024). */}
            <Typography variant="h1" component="h1" sx={{ color: 'text.primary' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: '0.8125rem', color: theme.custom?.text?.tertiary, mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Stepper */}
        <Stepper
          activeStep={wizard.activeStep}
          alternativeLabel={!isMobile}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{ mb: 4 }}
        >
          {STEP_LABELS.map((label) => (
            <Step key={label}>
              <StepLabel>{translate(label)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Box sx={{ minHeight: 300 }}>
          {/* Step 1: Select Device */}
          {wizard.activeStep === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MyFormSection
                  title={translate('resources.simulators.wizard.select_hotel')}
                  subtitle={translate('resources.simulators.wizard.select_hotel_desc')}
                  icon={<HotelIcon />}
                  variant="outlined"
                >
                  <TextField
                    label={translate('resources.simulators.config.hotel')}
                    value={wizard.hotelId}
                    onChange={(e) => {
                      wizard.setHotelId(e.target.value);
                      wizard.setDeviceId('');
                    }}
                    disabled={hotelsLoading}
                    fullWidth
                    size="small"
                    select
                  >
                    <MenuItem value="">
                      {translate('resources.simulators.config.hotel_select')}
                    </MenuItem>
                    {hotels?.map((hotel: any) => (
                      <MenuItem key={hotel.id} value={hotel.id}>
                        {hotel.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </MyFormSection>
              </Grid>

              {wizard.hotelId && (
                <Grid item xs={12} md={6}>
                  <MyFormSection
                    title={translate('resources.simulators.wizard.select_device')}
                    subtitle={translate('resources.simulators.wizard.select_device_desc')}
                    icon={<DeviceIcon />}
                    variant="outlined"
                  >
                    <DeviceSelector
                      hotelId={wizard.hotelId}
                      value={wizard.deviceId}
                      onChange={wizard.setDeviceId}
                      deviceType={deviceType}
                      channel={channel}
                    />
                  </MyFormSection>
                </Grid>
              )}
            </Grid>
          )}

          {/* Step 2: Configure */}
          {wizard.activeStep === 1 && (
            <Box>
              {configForm}
            </Box>
          )}

          {/* Step 3: Run & Results */}
          {wizard.activeStep === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Idle - show run button */}
              {wizard.simulationStatus === 'idle' && (
                <MyFormSection
                  title={translate('resources.simulators.wizard.ready_title')}
                  subtitle={translate('resources.simulators.wizard.ready_desc')}
                  icon={<RunIcon />}
                  variant="card"
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {translate('resources.simulators.wizard.ready_summary')}
                  </Typography>
                </MyFormSection>
              )}

              {/* Running - show progress */}
              {isRunning && (
                <SimulationProgress
                  status={wizard.simulationStatus}
                  phases={wizard.phases}
                  capturedRequests={wizard.capturedRequests}
                  simulatorName={title}
                />
              )}

              {/* Completed - show results */}
              {isCompleted && wizard.summary && (
                <SimulationResults
                  summary={wizard.summary}
                  logs={wizard.logs}
                  capturedRequests={wizard.capturedRequests}
                  simulatorName={title}
                >
                  {resultsExtra}
                </SimulationResults>
              )}
            </Box>
          )}
        </Box>

        {/* Navigation Buttons */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 4,
          pt: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}>
          {/* Left side */}
          <Box>
            {wizard.activeStep > 0 && !isRunning && (
              <Button
                onClick={wizard.goBack}
                startIcon={<BackIcon />}
                disabled={isRunning}
              >
                {translate('ra.action.back')}
              </Button>
            )}
          </Box>

          {/* Right side */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Step 0 & 1: Next button */}
            {wizard.activeStep < 2 && (
              <Button
                variant="contained"
                onClick={wizard.goNext}
                endIcon={<NextIcon />}
                disabled={!wizard.canGoNext}
              >
                {translate('ra.action.next') || 'Next'}
              </Button>
            )}

            {/* Step 2: Run/Stop/Reset buttons */}
            {wizard.activeStep === 2 && (
              <>
                {wizard.simulationStatus === 'idle' && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<RunIcon />}
                    onClick={onRun}
                  >
                    {translate('resources.simulators.wizard.run_test')}
                  </Button>
                )}

                {isRunning && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<StopIcon />}
                    onClick={wizard.stopSimulation}
                  >
                    {translate('resources.simulators.wizard.cancel')}
                  </Button>
                )}

                {isCompleted && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<ResetIcon />}
                      onClick={wizard.resetSimulation}
                    >
                      {translate('resources.simulators.wizard.new_test')}
                    </Button>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
